// Google Tag Manager loader + Consent Mode v2 default. Injected only when
// VITE_GTM_ID is set — mirrors the env-gated `cookieyes.ts` / `sentry.ts`
// pattern, so any environment that leaves it blank never loads the
// container.
//
// Consent Mode v2: the `default` state (everything denied) is pushed
// SYNCHRONOUSLY here, before the async GTM container script can execute.
// Import order in `main.tsx` (this after `./cookieyes`) does NOT guarantee
// CookieYes runs first — both are `async` external scripts, so which one
// executes first is a download race, not a source-order fact. Setting the
// default ourselves means no consent-gated tag can fire before the user
// chooses, whichever script wins that race. CookieYes (Support GCM = on)
// then pushes the `consent` `update` when the user makes a choice.
//
// The container script is marked `data-cookieyes="cookieyes-necessary"` so
// CookieYes's automatic script blocker never blocks GTM itself: GTM is the
// consent orchestrator, not a tracker — it must always load, and the tags
// inside it stay gated by the Consent Mode signals above.
//
// This is the runtime equivalent of GTM's standard <head> snippet. The
// <noscript> iframe half is intentionally omitted: this is a JS-required
// React SPA, so a client with JS disabled renders nothing to track.

const containerId = import.meta.env.VITE_GTM_ID;

if (
  typeof document !== 'undefined' &&
  typeof containerId === 'string' &&
  containerId.length > 0
) {
  const w = window as typeof window & { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];

  // gtag command shape: gtag('consent', 'default', {...}) reaches the
  // dataLayer as an array read by index — the form GTM/gtag.js consumes.
  const gtag = (...args: unknown[]): void => {
    w.dataLayer!.push(args);
  };
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  });

  w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  script.setAttribute('data-cookieyes', 'cookieyes-necessary');
  document.head.appendChild(script);
}
